import { useControl, type IControl, type MapRef } from 'react-map-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';

import {
  aoiPolygonAtoms,
  aoiPolygonLockedAtoms,
} from './inventory-surface/map-atoms';
import { useSurface } from './inventory-surface/surface';

const drawStyles = [
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill' as const,
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: { 'fill-color': '#1d891e', 'fill-opacity': 0 },
  },
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line' as const,
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: { 'line-color': '#1d891e', 'line-width': 3 },
  },
  {
    id: 'gl-draw-point',
    type: 'circle' as const,
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex']],
    paint: { 'circle-radius': 5, 'circle-color': '#1d891e' },
  },
  {
    id: 'gl-draw-point-mid',
    type: 'circle' as const,
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: { 'circle-radius': 3, 'circle-color': '#1d891e' },
  },
  {
    id: 'gl-draw-line',
    type: 'line' as const,
    filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
    paint: { 'line-color': '#1d891e', 'line-width': 3 },
  },
];

interface Props {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  initialPolygon?: GeoJSON.Polygon | null;
}

export const TcDrawPolygonControl = ({
  position = 'top-right',
  initialPolygon,
}: Props) => {
  const surface = useSurface();
  const setAoiPolygon = useSetAtom(aoiPolygonAtoms[surface]);
  const setLocked = useSetAtom(aoiPolygonLockedAtoms[surface]);
  const drawRef = useRef<MapboxDraw | null>(null);

  const applyPolygon = useCallback(
    (e: { features: GeoJSON.Feature[] }) => {
      if (e.features.length > 0) {
        const polygon = e.features[0].geometry as GeoJSON.Polygon;
        setAoiPolygon(polygon);
      }
    },
    [setAoiPolygon]
  );

  const onCreate = useCallback(
    (e: { features: GeoJSON.Feature[] }) => {
      applyPolygon(e);
      setLocked(true);
    },
    [applyPolygon, setLocked]
  );

  const onDelete = useCallback(() => {
    setAoiPolygon(null);
    setLocked(true);
  }, [setAoiPolygon, setLocked]);

  useControl<IControl>(
    () => {
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: !initialPolygon,
          trash: true,
        },
        defaultMode: initialPolygon ? 'simple_select' : 'draw_polygon',
        styles: drawStyles,
      });
      drawRef.current = draw;
      return draw as unknown as IControl;
    },
    ({ map }: { map: MapRef }) => {
      map.on('draw.create' as string, onCreate);
      map.on('draw.update' as string, applyPolygon);
      map.on('draw.delete' as string, onDelete);

      // Resilience: mapbox-gl-draw only adds its render layers once
      // `map.loaded()` returns true (it polls on a 16ms interval — see
      // mapbox-gl-draw/src/setup.js). A slow or never-settling background tile
      // source can hold `map.loaded()` false indefinitely, leaving the control
      // invisible — you draw, but nothing renders. If the map isn't loaded yet,
      // briefly report loaded() so the draw connects on its next poll, then
      // restore the honest implementation.
      const rawMap = map.getMap();
      if (
        rawMap &&
        !rawMap.loaded() &&
        !rawMap.getSource('mapbox-gl-draw-cold')
      ) {
        const realLoaded = rawMap.loaded.bind(rawMap);
        rawMap.loaded = () => true;
        setTimeout(() => {
          rawMap.loaded = realLoaded;
        }, 250);
      }

      if (initialPolygon && drawRef.current) {
        const ids = drawRef.current.add({
          type: 'Feature',
          geometry: initialPolygon,
          properties: {},
        });
        if (ids.length > 0) {
          drawRef.current.changeMode('direct_select', {
            featureId: ids[0],
          });
        }
      }
    },
    ({ map }: { map: MapRef }) => {
      map.off('draw.create' as string, onCreate);
      map.off('draw.update' as string, applyPolygon);
      map.off('draw.delete' as string, onDelete);
    },
    { position }
  );

  return null;
};
